/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useState } from 'react';

import { Button, Grid, Typography2 } from '@coursera/cds-core';
import { ThumbsDownFilledIcon, ThumbsDownIcon, ThumbsUpFilledIcon, ThumbsUpIcon } from '@coursera/cds-icons';

import { useAiSuggestionsContext } from 'bundles/grading/components/staff-graded-assignments/ai-suggestions/AiSuggestionsContext';
import type { AiSuggestionRating } from 'bundles/grading/utils/aiSuggestionsUtils';

import _t from 'i18n!nls/grading';

const styles = {
  container: css`
    margin-top: var(--cds-spacing-150);
  `,
  button: css`
    padding: 0;
    margin: 0 var(--cds-spacing-100);
  `,
};

const RateSuggestion = () => {
  const { currentAiReviewPartSuggestion, currentAiReviewPartSuggestionRating, trackSuggestionRating } =
    useAiSuggestionsContext();
  const [currentRating, setCurrentRating] = useState<AiSuggestionRating>(currentAiReviewPartSuggestionRating);
  const [buttonsAreDisabled, setButtonsAreDisabled] = useState(false);

  // once a rating is selected, it must remain selected for this user
  // ensure the state is updated when needed
  useEffect(() => {
    setCurrentRating(currentAiReviewPartSuggestionRating);
  }, [currentAiReviewPartSuggestionRating]);

  if (!currentAiReviewPartSuggestion) {
    return null;
  }

  const handleOnCLick = (rating: AiSuggestionRating) => {
    // if the user feedback choice is the same as the current one, this means it's unsetting, so set to null
    // otherwise set it to the one chosen
    const ratingToUpdate = currentRating === rating ? null : rating;

    // update state so the change is more instant to the user
    setButtonsAreDisabled(true);
    setCurrentRating(ratingToUpdate);
    trackSuggestionRating(ratingToUpdate);
    setButtonsAreDisabled(false);
  };

  return (
    <Grid container css={styles.container} alignItems="center">
      <Grid item xs={12}>
        <Typography2 variant="bodyPrimary" color="supportText" component="span">
          {_t('Rate this recommendation:')}
        </Typography2>
        <Button
          variant="ghost"
          css={styles.button}
          onClick={() => handleOnCLick('LIKE')}
          aria-label={_t('Like')}
          data-testid="ai-feedback-like"
          disabled={buttonsAreDisabled}
          title={_t('Like')}
        >
          {currentRating === 'LIKE' ? <ThumbsUpFilledIcon color="interactive" /> : <ThumbsUpIcon color="support" />}
        </Button>
        <Button
          variant="ghost"
          css={styles.button}
          onClick={() => handleOnCLick('DISLIKE')}
          aria-label={_t('Dislike')}
          data-testid="ai-feedback-dislike"
          disabled={buttonsAreDisabled}
          title={_t('Dislike')}
        >
          {currentRating === 'DISLIKE' ? (
            <ThumbsDownFilledIcon color="interactive" />
          ) : (
            <ThumbsDownIcon color="support" />
          )}
        </Button>
      </Grid>
    </Grid>
  );
};

export default RateSuggestion;
